import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';

interface AdminCancelOrderModalProps {
  orderId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export default function AdminCancelOrderModal({ orderId, onSuccess, onClose }: AdminCancelOrderModalProps) {
  const auth = useAuth();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters.');
      return;
    }
    if (!auth.token) {
      setError('Not authenticated.');
      return;
    }
    setSubmitting(true);
    try {
      await api.cancelAdminOrder(orderId, reason.trim(), auth.token);
      setToast(true);
      setTimeout(() => {
        setToast(false);
        onSuccess();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the order to disputes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: '440px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>Send to disputes</h3>
          <button className="admin-modal-close" onClick={onClose} disabled={submitting}>&times;</button>
        </div>

        <div className="admin-modal-body">
          {toast && (
            <div className="admin-info-banner success" style={{ marginBottom: '1rem' }}>
              Order sent to disputes.
            </div>
          )}

          <div className="admin-info-banner warning" style={{ marginBottom: '1rem' }}>
            This sets the order to <strong>DISPUTED</strong>. The garage&apos;s payout for it is held until you
            resolve the dispute — close it (with or without a refund) or return it to service.
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">
              Reason <span style={{ color: 'var(--admin-danger, #dc3545)' }}>*</span>
            </label>
            <textarea
              className="admin-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Why is this order being disputed? (min 10 characters)"
              disabled={submitting || toast}
            />
            <div style={{ fontSize: '0.75rem', color: reason.length < 10 && reason.length > 0 ? 'var(--admin-danger, #dc3545)' : 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
              {reason.length}/10 minimum characters
            </div>
          </div>

          {error && <div className="admin-info-banner danger">{error}</div>}
        </div>

        <div className="admin-modal-footer">
          <button
            className="admin-btn admin-btn-outline"
            onClick={onClose}
            disabled={submitting || toast}
          >
            Cancel
          </button>
          <button
            className="admin-btn admin-btn-danger"
            onClick={handleSubmit}
            disabled={submitting || toast || reason.trim().length < 10}
          >
            {submitting ? 'Sending…' : 'Send to disputes'}
          </button>
        </div>
      </div>
    </div>
  );
}
